import { useCallback, useEffect, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import Colors from "../styles/Colors.json";
import "./UnionWagesPage.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import config from "../configLoader";
import {
  getUnionClassifications,
  activateUnionClassification,
  deactivateUnionClassification,
  getUnionClassificationRates,
  postUnionClassificationRate,
  getErrorMessage,
} from "../api/unionClassification";
import { getPWFringeTypes } from "../api/prevailingWage";
import {
  UnionJobClassification,
  UnionClassificationRate,
} from "../interfaces/UnionClassification";
import { PWFringeType } from "../interfaces/PrevailingWage";
import {
  getUnionHolidayWorkedConfigs,
  putUnionHolidayWorkedConfig,
  activateUnionHolidayWorkedConfig,
  deactivateUnionHolidayWorkedConfig,
  getHolidayWorkedErrorMessage,
} from "../api/unionHolidayWorked";
import { UnionHolidayWorkedConfig } from "../interfaces/UnionHolidayWorked";
import { getLocations } from "../api/locations";
import { getHolidayCalendar } from "../api/timeOff";
import { Location } from "../interfaces/LocationsInterface";
import { HolidayCalendarEntry } from "../interfaces/TimeOff";
import {
  DollarSign,
  CalendarDays,
  MapPin,
  Plus,
  Check,
  ChevronDown,
  ChevronRight,
  X,
  AlertTriangle,
  Trash2,
  Info,
  CheckCircle,
  Ban,
  RotateCcw,
  ExternalLink,
  ChevronUp,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────
type TabId = "classifications" | "rates" | "holiday";

// ── Date helpers ──────────────────────────────────────────────────────────
const TODAY: string = new Date().toISOString().slice(0, 10);

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${MON[m - 1]} ${d}, ${y}`;
}
function fmtShort(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${MON[m - 1]} ${d}`;
}
function fmtMult(v: number): string {
  return v.toFixed(2);
}
function yearOf(iso: string): number {
  return Number(iso.slice(0, 4));
}
function dayOfWeek(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return DOW[new Date(y, m - 1, d).getDay()];
}
function isPast(iso: string): boolean {
  return iso < TODAY;
}
function money(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Rate helpers ──────────────────────────────────────────────────────────
function sortedRates(rates: UnionClassificationRate[]): UnionClassificationRate[] {
  return [...rates].sort(
    (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
  );
}

function currentRateOf(rates: UnionClassificationRate[]): UnionClassificationRate | null {
  const inForce = rates.filter((r) => r.effectiveDate <= TODAY).sort(
    (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
  );
  return inForce[0] ?? null;
}

// ── Toast component ───────────────────────────────────────────────────────
interface ToastState {
  msg: string;
  kind: "success" | "error";
  visible: boolean;
}

// ── Main component ────────────────────────────────────────────────────────
const UnionWagesPage: React.FC = () => {
  const { setLoading, createPopup, userRole, setPage, userID } = useSiteContext();

  // ── Tab ───────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<TabId>("classifications");

  // ── Toast ─────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<ToastState>({ msg: "", kind: "success", visible: false });
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string, kind: "success" | "error" = "success") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, kind, visible: true });
    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 2400);
  }, []);

  // ── Classifications & Rates data ──────────────────────────────────────
  const [classifications, setClassifications] = useState<UnionJobClassification[]>([]);
  const [ratesMap, setRatesMap] = useState<Record<number, UnionClassificationRate[]>>({});
  const [fringeTypes, setFringeTypes] = useState<PWFringeType[]>([]);

  // ── Tab 2: selected classification + expanded fringe rows ─────────────
  const [selectedPC, setSelectedPC] = useState<number | null>(null);
  const [expandedRateIDs, setExpandedRateIDs] = useState<Set<number>>(new Set());

  // ── Deactivate confirm (classifications) ──────────────────────────────
  const [confirmDeactivateCls, setConfirmDeactivateCls] = useState<UnionJobClassification | null>(null);

  // ── Add Rate modal ────────────────────────────────────────────────────
  const [addRateOpen, setAddRateOpen] = useState(false);
  const [rateEff, setRateEff] = useState("");
  const [rateMin, setRateMin] = useState("");
  const [rateCap, setRateCap] = useState("");
  const [rateNotes, setRateNotes] = useState("");
  const [rateFringes, setRateFringes] = useState<{ pwFringeTypeID: number; hourlyAmount: string }[]>([]);
  const [rateIsPrefilled, setRateIsPrefilled] = useState(false);
  const [rateEffErr, setRateEffErr] = useState("");
  const [rateMinErr, setRateMinErr] = useState("");
  const [rateCapErr, setRateCapErr] = useState("");

  // ── Holiday Pay data ──────────────────────────────────────────────────
  const [locations, setLocations] = useState<Location[]>([]);
  const [holidayCalendar, setHolidayCalendar] = useState<HolidayCalendarEntry[]>([]);
  const [holidayConfigs, setHolidayConfigs] = useState<UnionHolidayWorkedConfig[]>([]);

  // ── Holiday editor modal ──────────────────────────────────────────────
  const [holidayEditorOpen, setHolidayEditorOpen] = useState(false);
  const [editorLocationID, setEditorLocationID] = useState<number | null>(null);
  const [editorIsNew, setEditorIsNew] = useState(false);
  const [editorMultiplier, setEditorMultiplier] = useState("2.00");
  const [editorMultErr, setEditorMultErr] = useState(false);
  const [editorSelected, setEditorSelected] = useState<Set<number>>(new Set());
  const [editorNotes, setEditorNotes] = useState("");
  const [openYears, setOpenYears] = useState<Set<number>>(new Set([yearOf(TODAY)]));

  // ── Deactivate confirm (holiday) ──────────────────────────────────────
  const [confirmDeactivateHp, setConfirmDeactivateHp] = useState<UnionHolidayWorkedConfig | null>(null);

  // ── Access guard ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userRole.admin) {
      createPopup("Access Denied", "You do not have permission to access this page.");
      setPage(Page.Dashboard);
      return;
    }
    if (!config.showUnionClassificationPages) {
      createPopup("Access Denied", "This feature is not available.");
      setPage(Page.Dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // ── Load classifications + fringe types ───────────────────────────────
  const loadClassifications = useCallback(async () => {
    setLoading(true);
    try {
      const [clsData, ftData] = await Promise.all([
        getUnionClassifications(),
        getPWFringeTypes(true),
      ]);
      const sorted = [...clsData].sort((a, b) => a.sortOrder - b.sortOrder);
      setClassifications(sorted);
      setFringeTypes(ftData);

      const ratePairs = await Promise.all(
        sorted.map(async (c) => {
          const rates = await getUnionClassificationRates(c.projectClassification);
          return [c.projectClassification, rates] as [number, UnionClassificationRate[]];
        })
      );
      const map: Record<number, UnionClassificationRate[]> = {};
      for (const [pc, rates] of ratePairs) {
        map[pc] = rates;
      }
      setRatesMap(map);

      setSelectedPC((prev) => prev ?? (sorted[0]?.projectClassification ?? null));
    } catch (e) {
      createPopup("Error", getErrorMessage(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load holiday pay data ─────────────────────────────────────────────
  const loadHoliday = useCallback(async () => {
    setLoading(true);
    try {
      const [locsResp, calData, cfgData] = await Promise.all([
        getLocations(),
        getHolidayCalendar(),
        getUnionHolidayWorkedConfigs(),
      ]);
      setLocations((locsResp as { data: Location[] }).data);
      setHolidayCalendar(calData);
      setHolidayConfigs(cfgData);
    } catch (e) {
      createPopup("Error", getHolidayWorkedErrorMessage(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userRole.admin || !config.showUnionClassificationPages) return;
    loadClassifications();
    loadHoliday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole.admin]);

  // ── Esc to close modals ───────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (addRateOpen) { setAddRateOpen(false); return; }
      if (holidayEditorOpen) { setHolidayEditorOpen(false); return; }
      if (confirmDeactivateCls) { setConfirmDeactivateCls(null); return; }
      if (confirmDeactivateHp) { setConfirmDeactivateHp(null); return; }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [addRateOpen, holidayEditorOpen, confirmDeactivateCls, confirmDeactivateHp]);

  // ── Tab 1: Activate / Deactivate ─────────────────────────────────────
  const handleActivateCls = async (cls: UnionJobClassification) => {
    setLoading(true);
    try {
      await activateUnionClassification(cls.projectClassification);
      await loadClassifications();
      showToast(`${cls.name} activated`);
    } catch (err) {
      createPopup("Error", getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateClsConfirmed = async () => {
    if (!confirmDeactivateCls) return;
    const cls = confirmDeactivateCls;
    setConfirmDeactivateCls(null);
    setLoading(true);
    try {
      await deactivateUnionClassification(cls.projectClassification);
      await loadClassifications();
      showToast("Classification deactivated");
    } catch (err) {
      createPopup("Error", getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Tab 1: deep-link to Rate Schedule ────────────────────────────────
  const goToRateSchedule = (pc: number) => {
    setSelectedPC(pc);
    setTab("rates");
  };

  // ── Tab 2: expand/collapse fringe row ────────────────────────────────
  const toggleExpand = (rateID: number) => {
    setExpandedRateIDs((prev) => {
      const next = new Set(prev);
      if (next.has(rateID)) next.delete(rateID);
      else next.add(rateID);
      return next;
    });
  };

  // ── Tab 2: Add Rate modal ─────────────────────────────────────────────
  const openAddRateModal = (pc: number) => {
    const cur = currentRateOf(ratesMap[pc] ?? []);
    setRateEff("");
    setRateNotes("");
    setRateEffErr("");
    setRateMinErr("");
    setRateCapErr("");
    if (cur) {
      setRateMin(String(cur.minimumHourlyRate));
      setRateCap(String(cur.maxFringeHoursPerWeek));
      setRateFringes(
        cur.fringeItems.length > 0
          ? cur.fringeItems.map((f) => ({ pwFringeTypeID: f.pwFringeTypeID, hourlyAmount: String(f.hourlyAmount) }))
          : [{ pwFringeTypeID: fringeTypes[0]?.pwFringeTypeID ?? 0, hourlyAmount: "" }]
      );
      setRateIsPrefilled(true);
    } else {
      setRateMin("");
      setRateCap("");
      setRateFringes(fringeTypes.length > 0 ? [{ pwFringeTypeID: fringeTypes[0].pwFringeTypeID, hourlyAmount: "" }] : []);
      setRateIsPrefilled(false);
    }
    setAddRateOpen(true);
  };

  const totalFringe = rateFringes.reduce((s, r) => s + (parseFloat(r.hourlyAmount) || 0), 0);

  const updateFringeRow = (i: number, field: "pwFringeTypeID" | "hourlyAmount", val: string) => {
    setRateFringes((prev) =>
      prev.map((r, idx) =>
        idx === i ? { ...r, [field]: field === "pwFringeTypeID" ? Number(val) : val } : r
      )
    );
  };

  const addFringeRow = () => {
    const usedIDs = new Set(rateFringes.map((r) => r.pwFringeTypeID));
    const nextType = fringeTypes.find((ft) => !usedIDs.has(ft.pwFringeTypeID));
    setRateFringes((prev) => [
      ...prev,
      { pwFringeTypeID: nextType?.pwFringeTypeID ?? fringeTypes[0]?.pwFringeTypeID ?? 0, hourlyAmount: "" },
    ]);
  };

  const submitAddRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPC === null) return;

    let hasErr = false;
    let newEffErr = "";
    let newMinErr = "";
    let newCapErr = "";

    if (!rateEff) {
      newEffErr = "effectiveDate is required (ISO date string)";
      hasErr = true;
    } else {
      const existing = ratesMap[selectedPC] ?? [];
      if (existing.some((r) => r.effectiveDate === rateEff)) {
        newEffErr = "A rate record already exists for this classification and effective date";
        hasErr = true;
      }
    }
    const minVal = parseFloat(rateMin);
    if (isNaN(minVal) || minVal < 0) {
      newMinErr = "minimumHourlyRate must be a positive number";
      hasErr = true;
    }
    const capVal = parseFloat(rateCap);
    if (isNaN(capVal) || capVal < 0) {
      newCapErr = "maxFringeHoursPerWeek must be a positive number";
      hasErr = true;
    }

    setRateEffErr(newEffErr);
    setRateMinErr(newMinErr);
    setRateCapErr(newCapErr);

    if (hasErr) {
      showToast("Fix the highlighted fields", "error");
      return;
    }

    setLoading(true);
    try {
      await postUnionClassificationRate(selectedPC, {
        effectiveDate: rateEff,
        minimumHourlyRate: minVal,
        maxFringeHoursPerWeek: capVal,
        notes: rateNotes.trim() || null,
        fringeItems: rateFringes
          .filter((r) => r.pwFringeTypeID)
          .map((r) => ({ pwFringeTypeID: r.pwFringeTypeID, hourlyAmount: parseFloat(r.hourlyAmount) || 0 })),
      });
      const updated = await getUnionClassificationRates(selectedPC);
      setRatesMap((prev) => ({ ...prev, [selectedPC]: updated }));
      setAddRateOpen(false);
      setExpandedRateIDs(new Set());
      showToast("Rate added");
    } catch (err) {
      createPopup("Error", getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Tab 3: Holiday editor open ────────────────────────────────────────
  const openHolidayEditor = (locationID: number) => {
    const cfg = holidayConfigs.find((c) => c.locationID === locationID);
    setEditorLocationID(locationID);
    setEditorIsNew(!cfg);
    setEditorMultiplier(cfg ? fmtMult(cfg.multiplier) : "2.00");
    setEditorMultErr(false);
    setEditorSelected(new Set(cfg ? cfg.holidays.map((h) => h.holidayCalendarID) : []));
    setEditorNotes(cfg?.notes ?? "");
    setOpenYears(new Set([yearOf(TODAY)]));
    setHolidayEditorOpen(true);
  };

  // ── Tab 3: multiplier helpers ─────────────────────────────────────────
  const validMult = (v: number) => Number.isFinite(v) && v >= 1 && v <= 9.99;

  const stepMult = (dir: 1 | -1) => {
    let v = parseFloat(editorMultiplier);
    if (!Number.isFinite(v)) v = 1;
    v = Math.round((v + dir * 0.25) / 0.25) * 0.25;
    v = Math.min(9.99, Math.max(1, v));
    setEditorMultiplier(fmtMult(v));
    setEditorMultErr(false);
  };

  const multSideText = (): { cls: string; text: React.ReactNode } => {
    const v = parseFloat(editorMultiplier);
    if (!validMult(v)) return { cls: "", text: <span>Enter <b>1.00</b>–<b>9.99</b></span> };
    if (v <= 1) return { cls: "off", text: <span><b>No premium</b> — 1.00× is effectively off</span> };
    return {
      cls: "",
      text: (
        <>
          <b>{fmtMult(v)}×</b> the base hourly rate
          <br />
          <span style={{ color: "var(--text-3)" }}>e.g. $30.00 → ${(30 * v).toFixed(2)}/hr</span>
        </>
      ),
    };
  };

  // ── Tab 3: holiday picker helpers ─────────────────────────────────────
  const pickerEntries = (): HolidayCalendarEntry[] => {
    return holidayCalendar.filter(
      (e) => e.active || editorSelected.has(e.holidayCalendarID)
    );
  };

  const toggleHoliday = (id: number) => {
    setEditorSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleYear = (y: number) => {
    setOpenYears((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y);
      else next.add(y);
      return next;
    });
  };

  const clearAllHolidays = () => setEditorSelected(new Set());

  // ── Tab 3: Save holiday config ────────────────────────────────────────
  const saveHolidayConfig = async () => {
    if (editorLocationID === null) return;
    const v = parseFloat(editorMultiplier);
    if (!validMult(v)) {
      setEditorMultErr(true);
      showToast("Fix the multiplier before saving", "error");
      return;
    }
    setLoading(true);
    try {
      await putUnionHolidayWorkedConfig(editorLocationID, {
        multiplier: v,
        notes: editorNotes.trim() || null,
        adminEmployeeID: userID ?? null,
        holidayCalendarIDs: [...editorSelected],
      });
      const updated = await getUnionHolidayWorkedConfigs();
      setHolidayConfigs(updated);
      setHolidayEditorOpen(false);
      showToast(editorIsNew ? "Config created" : "Config saved");
    } catch (err) {
      createPopup("Error", getHolidayWorkedErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Tab 3: Activate / Deactivate holiday config ───────────────────────
  const handleActivateHp = async (cfg: UnionHolidayWorkedConfig) => {
    setLoading(true);
    try {
      await activateUnionHolidayWorkedConfig(cfg.locationID);
      const updated = await getUnionHolidayWorkedConfigs();
      setHolidayConfigs(updated);
      showToast("Config reactivated");
    } catch (err) {
      createPopup("Error", getHolidayWorkedErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateHpConfirmed = async () => {
    if (!confirmDeactivateHp) return;
    const cfg = confirmDeactivateHp;
    setConfirmDeactivateHp(null);
    setLoading(true);
    try {
      await deactivateUnionHolidayWorkedConfig(cfg.locationID);
      const updated = await getUnionHolidayWorkedConfigs();
      setHolidayConfigs(updated);
      showToast("Config deactivated");
    } catch (err) {
      createPopup("Error", getHolidayWorkedErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────

  /** Rate schedule cell text for the classifications tab */
  const renderRateCell = (cls: UnionJobClassification) => {
    const rates = ratesMap[cls.projectClassification] ?? [];
    const cur = currentRateOf(rates);

    if (cls.isActive && rates.length === 0) {
      return (
        <button
          type="button"
          className="uw-warnbadge"
          onClick={() => goToRateSchedule(cls.projectClassification)}
        >
          <AlertTriangle size={13} />
          No rates — payroll hazard
        </button>
      );
    }
    if (rates.length === 0) {
      return <span className="uw-rates-sub">No rates on file</span>;
    }
    if (cur) {
      return (
        <span className="uw-rates-sub">
          Current <b>${money(cur.minimumHourlyRate)}</b>/hr · {rates.length} rate{rates.length === 1 ? "" : "s"}
        </span>
      );
    }
    return (
      <span className="uw-rates-sub">
        {rates.length} upcoming · none in force yet
      </span>
    );
  };

  /** Rail sub line for the rate schedule tab */
  const renderRailSub = (cls: UnionJobClassification) => {
    const rates = ratesMap[cls.projectClassification] ?? [];
    const cur = currentRateOf(rates);
    if (cls.isActive && rates.length === 0) {
      return <span className="uw-rail-cur none">⚠ No rates</span>;
    }
    if (cur) {
      return <span className="uw-rail-cur">${money(cur.minimumHourlyRate)}/hr</span>;
    }
    if (rates.length > 0) {
      return <span className="uw-rail-cur none">Upcoming only</span>;
    }
    return <span className="uw-rail-cur none">No rates</span>;
  };

  // ── Summary counts ────────────────────────────────────────────────────
  const activeClsCount = classifications.filter((c) => c.isActive).length;
  const hazardCount = classifications.filter(
    (c) => c.isActive && (ratesMap[c.projectClassification] ?? []).length === 0
  ).length;

  const configuredLocs = locations.filter((loc) => holidayConfigs.some((c) => c.locationID === loc.locationID));
  const activeCfgCount = holidayConfigs.filter((c) => c.active).length;

  // ── Holiday upcoming chips for list view ──────────────────────────────
  const upcomingHolidays = (cfg: UnionHolidayWorkedConfig) =>
    [...cfg.holidays]
      .filter((h) => !isPast(h.holidayDate))
      .sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));

  // ── Fringe editor: used type IDs for disabling ────────────────────────
  const usedFringeIDs = new Set(rateFringes.map((r) => r.pwFringeTypeID));

  // ── Picker rows grouped by year ───────────────────────────────────────
  const pickerGrouped = (): { year: number; entries: HolidayCalendarEntry[] }[] => {
    const rows = pickerEntries();
    const years = [...new Set(rows.map((e) => yearOf(e.holidayDate)))].sort((a, b) => b - a);
    return years.map((y) => ({
      year: y,
      entries: rows.filter((e) => yearOf(e.holidayDate) === y).sort((a, b) => a.holidayDate.localeCompare(b.holidayDate)),
    }));
  };

  const selectedPC_cls = selectedPC !== null ? classifications.find((c) => c.projectClassification === selectedPC) : null;
  const editorLoc = editorLocationID !== null ? locations.find((l) => l.locationID === editorLocationID) : null;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <PageWrapper title="Union Wages" backgroundColor={Colors.White}>
      <div className="uw">

        {/* ── Page header ─────────────────────────────────────────────── */}
        <div className="uw-head">
          <div className="uw-logo">
            <DollarSign size={22} strokeWidth={2.5} />
          </div>
          <div>
            <div className="uw-page-title">Union Wages</div>
            <div className="uw-page-sub">Manage union classifications, rate schedules, and holiday pay premiums</div>
          </div>
        </div>

        {/* ── Tab bar ─────────────────────────────────────────────────── */}
        <div className="uw-tabs">
          <button
            type="button"
            className={`uw-tab${tab === "classifications" ? " on" : ""}`}
            onClick={() => setTab("classifications")}
          >
            <Check size={17} />
            Classifications
          </button>
          <button
            type="button"
            className={`uw-tab${tab === "rates" ? " on" : ""}`}
            onClick={() => setTab("rates")}
          >
            <DollarSign size={17} />
            Rate Schedule
          </button>
          <button
            type="button"
            className={`uw-tab${tab === "holiday" ? " on" : ""}`}
            onClick={() => setTab("holiday")}
          >
            <CalendarDays size={17} />
            Holiday Pay
          </button>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            TAB 1 — CLASSIFICATIONS
            ════════════════════════════════════════════════════════════ */}
        {tab === "classifications" && (
          <>
            {/* Summary line */}
            <div className="uw-summary">
              <span><b>{classifications.length}</b> classifications</span>
              <span className="sep" />
              <span><b>{activeClsCount}</b> active</span>
              {hazardCount > 0 && (
                <>
                  <span className="sep" />
                  <span className="hazard">{hazardCount} active with no rates</span>
                </>
              )}
            </div>

            <div className="uw-ccard">
              <table className="uw-ctable">
                <thead>
                  <tr>
                    <th>Classification</th>
                    <th>Rate schedule</th>
                    <th>Status</th>
                    <th className="acts">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classifications.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", color: "var(--text-3)", padding: "40px" }}>
                        No classifications found.
                      </td>
                    </tr>
                  )}
                  {classifications.map((cls) => (
                    <tr key={cls.unionJobClassificationID} className={cls.isActive ? "" : "is-inactive"}>
                      <td>
                        <div className="uw-class-cell">
                          <span className="uw-class-ic">{cls.projectClassification}</span>
                          <div>
                            <div className="uw-class-name">{cls.name}</div>
                            <div className="uw-class-key">Classification {cls.projectClassification}</div>
                          </div>
                        </div>
                      </td>
                      <td>{renderRateCell(cls)}</td>
                      <td>
                        <span className={`uw-pill ${cls.isActive ? "active" : "inactive"}`}>
                          <span className="uw-dot" />
                          {cls.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="rowacts">
                          <button
                            type="button"
                            className="uw-abtn"
                            onClick={() => goToRateSchedule(cls.projectClassification)}
                          >
                            <DollarSign size={14} />
                            Rate schedule
                          </button>
                          {cls.isActive ? (
                            <button
                              type="button"
                              className="uw-abtn"
                              onClick={() => setConfirmDeactivateCls(cls)}
                            >
                              <Ban size={14} />
                              Deactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="uw-abtn"
                              onClick={() => handleActivateCls(cls)}
                            >
                              <RotateCcw size={14} />
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB 2 — RATE SCHEDULE (master–detail)
            ════════════════════════════════════════════════════════════ */}
        {tab === "rates" && (
          <div className="uw-md-layout">
            {/* Left rail */}
            <div className="uw-policy-list">
              <div className="pl-head">
                <span className="t">Classifications</span>
                <span className="c">{classifications.length}</span>
              </div>
              {classifications.map((cls) => (
                <button
                  key={cls.projectClassification}
                  type="button"
                  className={`uw-policy-item${selectedPC === cls.projectClassification ? " on" : ""}${cls.isActive ? "" : " is-inactive"}`}
                  onClick={() => { setSelectedPC(cls.projectClassification); setExpandedRateIDs(new Set()); }}
                >
                  <div className="nm">
                    <span className="uw-class-ic" style={{ width: 26, height: 26, fontSize: 12 }}>
                      {cls.projectClassification}
                    </span>
                    {cls.name}
                  </div>
                  <div className="metaline">
                    <span className={`uw-pill ${cls.isActive ? "active" : "inactive"}`} style={{ fontSize: 10, padding: "3px 8px 3px 7px" }}>
                      <span className="uw-dot" />
                      {cls.isActive ? "Active" : "Inactive"}
                    </span>
                    {renderRailSub(cls)}
                  </div>
                </button>
              ))}
            </div>

            {/* Right detail */}
            {selectedPC_cls ? (
              <div className="uw-policy-detail">
                {/* Detail header */}
                <div className="uw-pd-head">
                  <div style={{ flex: 1 }}>
                    <div className="uw-pd-title">
                      {selectedPC_cls.name}
                      <span className={`uw-pill ${selectedPC_cls.isActive ? "active" : "inactive"}`} style={{ fontSize: 11 }}>
                        <span className="uw-dot" />
                        {selectedPC_cls.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="uw-pd-sub">
                      <span className="uw-mono">Classification {selectedPC_cls.projectClassification}</span>
                      {" · "}Rates are append-only — to change a rate, add a new record with a later effective date.
                    </div>
                  </div>
                  <div className="uw-pd-actions">
                    <button
                      type="button"
                      className="uw-manage-link"
                      onClick={() => setPage(Page.PWFringeTypes)}
                    >
                      Manage fringe types
                      <ExternalLink size={12} />
                    </button>
                    <button
                      type="button"
                      className="uw-btn primary"
                      onClick={() => openAddRateModal(selectedPC_cls.projectClassification)}
                    >
                      <Plus size={16} />
                      Add rate
                    </button>
                  </div>
                </div>

                {/* Rate history */}
                {(() => {
                  const rates = sortedRates(ratesMap[selectedPC_cls.projectClassification] ?? []);
                  const cur = currentRateOf(ratesMap[selectedPC_cls.projectClassification] ?? []);
                  const curID = cur?.unionClassificationRateID ?? null;

                  if (rates.length === 0) {
                    return (
                      <div className="uw-mini-empty">
                        <DollarSign size={28} strokeWidth={1.5} />
                        <div className="et">No rates defined yet</div>
                        <p>
                          {selectedPC_cls.isActive
                            ? <>This classification is <b>active but has no rate</b> — a payroll hazard. Add a rate so union wages can be applied.</>
                            : "No rate history on file for this classification."}
                        </p>
                        <button
                          type="button"
                          className="uw-btn primary"
                          style={{ marginTop: 6 }}
                          onClick={() => openAddRateModal(selectedPC_cls.projectClassification)}
                        >
                          <Plus size={16} />
                          Add rate
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div style={{ margin: "0 20px 20px" }}>
                      <table className="uw-ctable uw-rate-table">
                        <thead>
                          <tr>
                            <th>Effective date</th>
                            <th className="money">Min rate ($/hr)</th>
                            <th className="money">Fringe cap (h/wk)</th>
                            <th className="money">Total fringe ($/hr)</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rates.map((rate) => {
                            const isCur = rate.unionClassificationRateID === curID;
                            const isUp = rate.effectiveDate > TODAY;
                            const isOpen = expandedRateIDs.has(rate.unionClassificationRateID);
                            return [
                              <tr
                                key={rate.unionClassificationRateID}
                                className={`rate-row${isCur ? " current" : ""}${isUp ? " upcoming" : ""}${isOpen ? " open" : ""}`}
                              >
                                <td>
                                  <div className="eff">
                                    <button
                                      type="button"
                                      className="expandbtn"
                                      onClick={() => toggleExpand(rate.unionClassificationRateID)}
                                      title={isOpen ? "Collapse fringe breakdown" : "Expand fringe breakdown"}
                                    >
                                      <ChevronDown size={16} />
                                    </button>
                                    <span className="d">{fmtDate(rate.effectiveDate)}</span>
                                    {isCur && <span className="uw-tag-current">Current</span>}
                                    {isUp && <span className="uw-tag-upcoming">Upcoming</span>}
                                  </div>
                                </td>
                                <td className="money">${money(rate.minimumHourlyRate)}</td>
                                <td className="money">{rate.maxFringeHoursPerWeek}</td>
                                <td className="money">${money(rate.totalFringeRate)}</td>
                                <td className="dim">{rate.notes ?? "—"}</td>
                              </tr>,
                              <tr key={`fb-${rate.unionClassificationRateID}`} className="uw-fringe-row-detail">
                                <td colSpan={5}>
                                  <div className="uw-fringe-wrap">
                                    <div className="uw-fw-title">Fringe benefit breakdown</div>
                                    {rate.fringeItems.length > 0 ? (
                                      <>
                                        <div className="uw-fbreak">
                                          {rate.fringeItems.map((f) => (
                                            <div key={f.pwFringeTypeID} className="fb">
                                              <span className="fn">{f.fringeTypeName}</span>
                                              <span className="fv">${money(f.hourlyAmount)}</span>
                                            </div>
                                          ))}
                                        </div>
                                        <div className="uw-fbreak-total">
                                          Total fringe <b>${money(rate.totalFringeRate)}</b>/hr across {rate.fringeItems.length} item{rate.fringeItems.length === 1 ? "" : "s"}
                                        </div>
                                      </>
                                    ) : (
                                      <div className="uw-fbreak-empty">No itemized fringe benefits on this rate.</div>
                                    )}
                                  </div>
                                </td>
                              </tr>,
                            ];
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="uw-policy-detail uw-mini-empty">
                <DollarSign size={28} strokeWidth={1.5} />
                <div className="et">Select a classification</div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB 3 — HOLIDAY PAY
            ════════════════════════════════════════════════════════════ */}
        {tab === "holiday" && (
          <>
            {/* Summary */}
            {locations.length > 0 && (
              <div className="uw-summary">
                <span><b>{configuredLocs.length}</b> of <b>{locations.length}</b> locations configured</span>
                <span className="sep" />
                <span><b>{activeCfgCount}</b> active</span>
                <span className="sep" />
                <span><b>{locations.length - configuredLocs.length}</b> not configured</span>
              </div>
            )}

            {locations.length === 0 ? (
              <div className="uw-hp-empty">
                <div className="eic"><MapPin size={28} strokeWidth={1.5} /></div>
                <div className="et">No locations found</div>
                <p className="es">No locations are configured in the system.</p>
              </div>
            ) : holidayConfigs.length === 0 && locations.every((l) => !holidayConfigs.some((c) => c.locationID === l.locationID)) ? (
              <div className="uw-hp-empty">
                <div className="eic"><CalendarDays size={28} strokeWidth={1.5} /></div>
                <div className="et">No holiday pay configured</div>
                <p className="es">Add a config for a location to enable the holiday-worked premium for its employees.</p>
              </div>
            ) : null}

            {locations.length > 0 && (
              <div className="uw-ccard">
                <table className="uw-ctable">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Multiplier</th>
                      <th>Qualifying holidays</th>
                      <th>Status &amp; notes</th>
                      <th className="acts">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc) => {
                      const cfg = holidayConfigs.find((c) => c.locationID === loc.locationID);
                      const locCell = (
                        <div className={`uw-loc-cell${cfg && cfg.active ? " on" : ""}`}>
                          <span className="uw-loc-ic">
                            <MapPin size={17} />
                          </span>
                          <div>
                            <div className="uw-loc-nm">{loc.name}</div>
                          </div>
                        </div>
                      );

                      if (!cfg) {
                        return (
                          <tr key={loc.locationID} className="unconfig">
                            <td>{locCell}</td>
                            <td colSpan={3}>
                              <span className="uw-unconfig-tag">
                                <span className="dot" />
                                Not configured — holiday-worked premium does not apply here
                              </span>
                            </td>
                            <td>
                              <div className="rowacts">
                                <button
                                  type="button"
                                  className="uw-abtn add"
                                  onClick={() => openHolidayEditor(loc.locationID)}
                                >
                                  <Plus size={14} />
                                  Add config
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      const isOff = cfg.multiplier <= 1;
                      const up = upcomingHolidays(cfg);
                      const moreN = cfg.holidays.length - Math.min(2, up.length);

                      return (
                        <tr key={loc.locationID} className={cfg.active ? "" : "is-inactive"}>
                          <td>{locCell}</td>
                          <td>
                            <span className={`uw-mult${isOff ? " off" : ""}`}>
                              {fmtMult(cfg.multiplier)}<span className="x">×</span>
                            </span>
                          </td>
                          <td>
                            <div className="uw-holcount">
                              <span className="n">{cfg.holidays.length}</span> selected
                            </div>
                            <div className="uw-holcell" style={{ marginTop: 5 }}>
                              {up.slice(0, 2).map((h) => (
                                <span key={h.holidayCalendarID} className="uw-holchip">
                                  <span>{h.name}</span>
                                  <span className="d">{fmtShort(h.holidayDate)}</span>
                                </span>
                              ))}
                              {moreN > 0 && <span className="uw-holmore">+{moreN} more</span>}
                            </div>
                          </td>
                          <td>
                            <span className={`uw-pill ${cfg.active ? "active" : "inactive"}`}>
                              <span className="uw-dot" />
                              {cfg.active ? "Active" : "Inactive"}
                            </span>
                            <div className={`uw-notes-cell${!cfg.notes ? " empty" : ""}`}>
                              {cfg.notes || "—"}
                            </div>
                          </td>
                          <td>
                            <div className="rowacts">
                              <button
                                type="button"
                                className="uw-abtn"
                                onClick={() => openHolidayEditor(loc.locationID)}
                              >
                                Edit
                              </button>
                              {cfg.active ? (
                                <button
                                  type="button"
                                  className="uw-abtn"
                                  onClick={() => setConfirmDeactivateHp(cfg)}
                                >
                                  <Ban size={14} />
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="uw-abtn"
                                  onClick={() => handleActivateHp(cfg)}
                                >
                                  <RotateCcw size={14} />
                                  Reactivate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════
            DEACTIVATE CONFIRM — Classifications
            ════════════════════════════════════════════════════════════ */}
        {confirmDeactivateCls && (
          <div className="uw-modal-overlay" onClick={() => setConfirmDeactivateCls(null)}>
            <div className="uw-modal uw-confirm" style={{ width: "min(480px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-head warn">
                <span className="mi"><Ban size={19} /></span>
                <div>
                  <h3>Deactivate classification?</h3>
                </div>
                <button type="button" className="uw-modal m-close" style={{ marginLeft: "auto" }} onClick={() => setConfirmDeactivateCls(null)}>
                  <X size={16} />
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Union wages will stop being applied to <b>new</b> timesheets for projects with the{" "}
                  <b>{confirmDeactivateCls.name}</b> classification. Previously submitted timesheets are{" "}
                  <b>unaffected</b>, and the full rate history is kept — reactivate anytime to restore it as-is.
                </p>
              </div>
              <div className="modal-foot">
                <button type="button" className="uw-btn ghost" onClick={() => setConfirmDeactivateCls(null)}>
                  Cancel
                </button>
                <button type="button" className="uw-btn danger-solid" onClick={handleDeactivateClsConfirmed}>
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            ADD RATE MODAL
            ════════════════════════════════════════════════════════════ */}
        {addRateOpen && selectedPC !== null && (
          <div className="uw-modal-overlay" onClick={() => setAddRateOpen(false)}>
            <div className="uw-modal wide" onClick={(e) => e.stopPropagation()}>
              <div className="m-head">
                <span className="m-ic"><DollarSign size={21} /></span>
                <div>
                  <h3>Add rate</h3>
                  <div className="m-sub">
                    {selectedPC_cls?.name} · <span className="uw-mono">Classification {selectedPC}</span>
                  </div>
                </div>
                <button type="button" className="m-close" onClick={() => setAddRateOpen(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className="m-body">
                {rateIsPrefilled && (() => {
                  const cur = currentRateOf(ratesMap[selectedPC] ?? []);
                  return (
                    <div className="uw-prefill-note">
                      <Info size={14} />
                      <span>
                        Prefilled from the current rate (<b>${cur ? money(cur.minimumHourlyRate) : "—"}/hr</b>,
                        eff. {cur ? fmtDate(cur.effectiveDate) : "—"}). Set a new effective date and adjust the numbers.
                      </span>
                    </div>
                  );
                })()}

                <div className="uw-fgrid">
                  {/* Effective date */}
                  <div className={`uw-frow${rateEffErr ? " err" : ""}`}>
                    <label className="uw-flabel">Effective date <span className="req">*</span></label>
                    <input
                      type="date"
                      className="uw-input"
                      value={rateEff}
                      onChange={(e) => { setRateEff(e.target.value); setRateEffErr(""); }}
                    />
                    {rateEffErr && (
                      <div className="uw-ferr">
                        <AlertTriangle size={13} />
                        <span>{rateEffErr}</span>
                      </div>
                    )}
                  </div>

                  {/* Minimum hourly rate */}
                  <div className={`uw-frow${rateMinErr ? " err" : ""}`}>
                    <label className="uw-flabel">Minimum hourly rate <span className="req">*</span></label>
                    <div className="uw-inwrap prefix">
                      <span className="pre">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="uw-input uw-mono"
                        style={{ textAlign: "right" }}
                        value={rateMin}
                        placeholder="0.00"
                        onChange={(e) => { setRateMin(e.target.value); setRateMinErr(""); }}
                      />
                    </div>
                    {rateMinErr && (
                      <div className="uw-ferr">
                        <AlertTriangle size={13} />
                        <span>{rateMinErr}</span>
                      </div>
                    )}
                  </div>

                  {/* Weekly fringe cap */}
                  <div className={`uw-frow${rateCapErr ? " err" : ""}`}>
                    <label className="uw-flabel">Weekly fringe cap <span className="req">*</span></label>
                    <div className="uw-inwrap">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="uw-input uw-mono"
                        style={{ textAlign: "right" }}
                        value={rateCap}
                        placeholder="40"
                        onChange={(e) => { setRateCap(e.target.value); setRateCapErr(""); }}
                      />
                      <span className="suffix">h / wk</span>
                    </div>
                    {rateCapErr && (
                      <div className="uw-ferr">
                        <AlertTriangle size={13} />
                        <span>{rateCapErr}</span>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="uw-frow">
                    <label className="uw-flabel">Notes <span className="opt">optional</span></label>
                    <input
                      type="text"
                      className="uw-input"
                      maxLength={120}
                      placeholder="e.g. 2026 CBA increase"
                      value={rateNotes}
                      onChange={(e) => setRateNotes(e.target.value)}
                    />
                  </div>

                  {/* Fringe section divider */}
                  <div className="uw-fsection full">
                    <span className="t">Fringe benefits</span>
                    <span className="ln" />
                  </div>

                  {/* Fringe editor */}
                  <div className="uw-frow full">
                    <div className="uw-fringe-editor">
                      <div className="fe-head">
                        <span className="t">Fringe type</span>
                        <span className="grow" />
                        <span className="t" style={{ width: 130 }}>Amount ($/hr)</span>
                        <span style={{ width: 34 }} />
                      </div>
                      <div className="fe-rows">
                        {rateFringes.length === 0 && (
                          <div className="fe-empty">No fringe items — total fringe is $0.00.</div>
                        )}
                        {rateFringes.map((row, i) => (
                          <div key={i} className="uw-frrow">
                            <select
                              className="uw-select"
                              value={row.pwFringeTypeID}
                              onChange={(e) => updateFringeRow(i, "pwFringeTypeID", e.target.value)}
                            >
                              <option value="" disabled>Select fringe type…</option>
                              {fringeTypes.map((ft) => (
                                <option
                                  key={ft.pwFringeTypeID}
                                  value={ft.pwFringeTypeID}
                                  disabled={usedFringeIDs.has(ft.pwFringeTypeID) && ft.pwFringeTypeID !== row.pwFringeTypeID}
                                >
                                  {ft.name}
                                </option>
                              ))}
                            </select>
                            <div className="amt">
                              <span className="pre">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="uw-input"
                                placeholder="0.00"
                                value={row.hourlyAmount}
                                onChange={(e) => updateFringeRow(i, "hourlyAmount", e.target.value)}
                              />
                            </div>
                            <button
                              type="button"
                              className="rm"
                              onClick={() => setRateFringes((prev) => prev.filter((_, idx) => idx !== i))}
                              title="Remove"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="fe-foot">
                        <button
                          type="button"
                          className="uw-addfringe"
                          onClick={addFringeRow}
                          disabled={rateFringes.length >= fringeTypes.length}
                        >
                          <Plus size={15} />
                          Add fringe item
                        </button>
                        <span className="uw-fringe-total-live">
                          Total fringe: <b>${money(totalFringe)}</b>/hr
                        </span>
                      </div>
                    </div>
                    <div className="uw-hint" style={{ marginTop: 8 }}>
                      Select from active PW fringe types. Zero rows is valid. Weekly cap is in <b>hours</b>, fringe amounts are <b>$/hr</b>.
                    </div>
                  </div>
                </div>
              </div>
              <div className="m-foot">
                <div className="hint">
                  <Info size={13} />
                  To update a rate later, add a new record with a later effective date — history is append-only.
                </div>
                <button type="button" className="uw-btn ghost" onClick={() => setAddRateOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="uw-btn primary" onClick={submitAddRate}>
                  <Check size={16} />
                  Add rate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            HOLIDAY EDITOR MODAL
            ════════════════════════════════════════════════════════════ */}
        {holidayEditorOpen && editorLoc && (
          <div className="uw-modal-overlay" onClick={() => setHolidayEditorOpen(false)}>
            <div className="uw-modal wide" onClick={(e) => e.stopPropagation()}>
              <div className="m-head">
                <span className="m-ic"><CalendarDays size={21} /></span>
                <div>
                  <h3>{editorIsNew ? "Add holiday-worked config" : "Edit holiday-worked config"}</h3>
                  <div className="m-sub">
                    {editorLoc.name} · <span className="uw-mono">LOC-{editorLoc.locationID}</span>
                  </div>
                </div>
                <button type="button" className="m-close" onClick={() => setHolidayEditorOpen(false)}>
                  <X size={16} />
                </button>
              </div>

              <div className="m-body">
                <div className="uw-fgrid">

                  {/* ── Premium section ── */}
                  <div className="uw-fsection full">
                    <span className="t">Premium</span>
                    <span className="ln" />
                  </div>

                  <div className={`uw-frow full${editorMultErr ? " uw-mult-err" : ""}`}>
                    <label className="uw-flabel">Holiday multiplier <span className="req">*</span></label>
                    <div className="uw-multiplier-field">
                      <div className={`uw-mult-input-wrap${editorMultErr ? " err" : ""}`}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="uw-input"
                          value={editorMultiplier}
                          onChange={(e) => {
                            setEditorMultiplier(e.target.value);
                            setEditorMultErr(false);
                          }}
                          onBlur={() => {
                            const v = parseFloat(editorMultiplier);
                            if (Number.isFinite(v)) setEditorMultiplier(fmtMult(Math.min(9.99, Math.max(1, v))));
                          }}
                        />
                        <span className="xsuffix">×</span>
                      </div>
                      <div className="uw-mult-steppers">
                        <button type="button" onClick={() => stepMult(1)}>
                          <ChevronUp size={15} />
                        </button>
                        <button type="button" onClick={() => stepMult(-1)}>
                          <ChevronDown size={15} />
                        </button>
                      </div>
                      <div className="uw-mult-side">
                        <span className={`mval${multSideText().cls ? ` ${multSideText().cls}` : ""}`}>
                          {multSideText().text}
                        </span>
                      </div>
                    </div>
                    <div className="uw-field-err">
                      <AlertTriangle size={14} />
                      <span>multiplier must be a number between 1 and 9.99</span>
                    </div>
                    <div className="uw-hint" style={{ marginTop: 8 }}>
                      Each hour that day pays the <b>greater of</b> this rate or its normal REG/OT/DT rate — the premium never reduces pay. Prevailing-wage project hours keep their PW rate.
                    </div>
                  </div>

                  {/* ── Qualifying holidays section ── */}
                  <div className="uw-fsection full">
                    <span className="t">Qualifying holidays</span>
                    <span className="ln" />
                  </div>

                  <div className="uw-frow full">
                    <div className="uw-hint" style={{ marginBottom: 8 }}>
                      Select which dated holidays trigger the premium at this location. Holidays are managed on the{" "}
                      <button
                        type="button"
                        className="uw-manage-link"
                        onClick={() => { setHolidayEditorOpen(false); setPage(Page.TimeOffConfiguration); }}
                      >
                        Time Off Configuration
                      </button>{" "}
                      page — this only selects them.
                    </div>

                    {/* Holiday picker */}
                    <div className="uw-picker">
                      <div className="uw-picker-top">
                        <span className="sel-count">
                          <span className="n">{editorSelected.size}</span> holiday{editorSelected.size === 1 ? "" : "s"} selected
                        </span>
                        <span className="grow" />
                        <button
                          type="button"
                          className="uw-linkbtn"
                          onClick={clearAllHolidays}
                          disabled={editorSelected.size === 0}
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="uw-picker-scroll">
                        {pickerGrouped().map(({ year, entries }) => {
                          const isOpen = openYears.has(year);
                          const selInYear = entries.filter((e) => editorSelected.has(e.holidayCalendarID)).length;
                          const isCurrentYear = year === yearOf(TODAY);
                          const isPastYear = year < yearOf(TODAY);
                          return (
                            <div key={year} className={`uw-yeargroup${isOpen ? " open" : ""}`}>
                              <button
                                type="button"
                                className="uw-yg-head"
                                onClick={() => toggleYear(year)}
                              >
                                <span className="uw-yg-chev"><ChevronRight size={15} /></span>
                                <span className={`uw-yg-year${isPastYear ? " past" : ""}`}>{year}</span>
                                {isCurrentYear && <span className="uw-yg-thisyeartag">This year</span>}
                                {isPastYear && <span className="uw-yg-pasttag">Past</span>}
                                <span className="uw-yg-meta">
                                  <span className={`uw-yg-selcount${selInYear === 0 ? " zero" : ""}`}>
                                    {selInYear} of {entries.length}
                                  </span>
                                </span>
                              </button>
                              <div className="uw-yg-body">
                                {entries.map((entry) => {
                                  const sel = editorSelected.has(entry.holidayCalendarID);
                                  const past = isPast(entry.holidayDate);
                                  return (
                                    <label
                                      key={entry.holidayCalendarID}
                                      className={`uw-holrow${sel ? " sel" : ""}${past ? " past" : ""}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={sel}
                                        onChange={() => toggleHoliday(entry.holidayCalendarID)}
                                      />
                                      <div className="hr-main">
                                        <div className="hr-name">
                                          {entry.name}
                                          {!entry.active && <span className="uw-inactive-tag">Inactive</span>}
                                          {past && <span className="uw-past-tag">Past</span>}
                                        </div>
                                        <div className="hr-sub">{dayOfWeek(entry.holidayDate)}</div>
                                      </div>
                                      <span className="hr-date">{entry.holidayDate}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {pickerGrouped().length === 0 && (
                          <div style={{ padding: "16px 13px", fontSize: 13, color: "var(--text-3)" }}>
                            No holidays in the calendar.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Notes section ── */}
                  <div className="uw-fsection full">
                    <span className="t">Notes</span>
                    <span className="ln" />
                  </div>

                  <div className="uw-frow full">
                    <div className="uw-lblrow">
                      <label className="uw-flabel">Notes <span className="opt">optional</span></label>
                      <span className={`uw-notes-count${editorNotes.length >= 200 ? " over" : ""}`}>
                        {editorNotes.length} / 200
                      </span>
                    </div>
                    <textarea
                      className="uw-textarea"
                      maxLength={200}
                      placeholder="e.g. Local 150 CBA 2026 — reference the agreement or effective date."
                      value={editorNotes}
                      onChange={(e) => setEditorNotes(e.target.value)}
                    />
                  </div>

                </div>
              </div>

              <div className="m-foot">
                <div className="hint">
                  <Info size={13} />
                  Applies to any timecard processed after saving. No effective-dating.
                </div>
                <button type="button" className="uw-btn ghost" onClick={() => setHolidayEditorOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="uw-btn primary" onClick={saveHolidayConfig}>
                  <Check size={16} />
                  {editorIsNew ? "Create config" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            DEACTIVATE CONFIRM — Holiday Pay
            ════════════════════════════════════════════════════════════ */}
        {confirmDeactivateHp && (
          <div className="uw-modal-overlay" onClick={() => setConfirmDeactivateHp(null)}>
            <div
              className="uw-modal uw-confirm"
              style={{ width: "min(480px, 96vw)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-head warn">
                <span className="mi"><Ban size={19} /></span>
                <div>
                  <h3>Deactivate holiday pay?</h3>
                </div>
                <button type="button" className="m-close uw-modal" style={{ marginLeft: "auto" }} onClick={() => setConfirmDeactivateHp(null)}>
                  <X size={16} />
                </button>
              </div>
              <div className="modal-body">
                <p>
                  The holiday-worked premium will stop applying for{" "}
                  <b>{locations.find((l) => l.locationID === confirmDeactivateHp.locationID)?.name ?? `LOC-${confirmDeactivateHp.locationID}`}</b>.
                  The multiplier and holiday selection are <b>kept</b> — reactivate anytime to restore them as-is.
                </p>
              </div>
              <div className="modal-foot">
                <button type="button" className="uw-btn ghost" onClick={() => setConfirmDeactivateHp(null)}>
                  Cancel
                </button>
                <button type="button" className="uw-btn danger-solid" onClick={handleDeactivateHpConfirmed}>
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Toast ────────────────────────────────────────────────────── */}
        <div className={`uw-toast${toast.visible ? " show" : ""}${toast.kind === "error" ? " error" : ""}`}>
          {toast.kind === "error" ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>

      </div>
    </PageWrapper>
  );
};

export default UnionWagesPage;
